import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'
import { connect, FormikValues } from 'formik'
import { flatten, get, isEmpty } from 'lodash'
import { useEffect, useRef, useState } from 'react'
import { IntlShape, useIntl } from 'react-intl'
import { useQueries, UseQueryOptions } from 'react-query'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import { array, object, string } from 'yup'

import { Select } from '@/components/Form'
import { MentionsRichTextEditorWithFormik } from '@/components/Form/MentionsRichTextEditor/MentionsRichTextEditor'
import InlineLoader from '@/components/InlineLoader'
import { FormLabel, Row } from '@/components/StyledComponents'
import {
  StyledWandIcon,
  VariableButton,
  VariableInputContainer
} from '@/pages/SpaceAppBuilder/components/StyledComponents'
import VariableOverlay from '@/pages/SpaceAppBuilder/components/VariableInput/VariableOverlay'
import { getRequestPreview } from '@/pages/SpaceAppBuilder/FeaturesPage/utils'
import { useWorkflowFeature } from '@/pages/SpaceAppBuilder/FeaturesPage/WorkflowFeatureContext'
import {
  APIRequestStepData,
  AppInstanceDataSource,
  DataSource,
  FieldTypes,
  StepData,
  Variable,
  WorkgridCatalogDatasource,
  WorkgridDatasource
} from '@/pages/SpaceAppBuilder/utilities/interfaces'
import { dataSourceV3CatalogQueryOptions, dataSourceV3QueryOptions } from '@/pages/SpaceAppBuilder/utilities/queries'
import { getAllFlattenedVariables } from '@/pages/SpaceAppBuilder/utilities/variableHelpers'

import messages from './messages'
import operationsMessages from './messages'
import QueryParameters from './QueryParameters'

//<editor-fold desc="Interfaces">
type APIRequestProps = {
  readOnly?: boolean
}
//</editor-fold>

//<editor-fold desc="Styled Components">
const StyledRow = styled(Row)`
  justify-content: flex-start;
  margin-bottom: 5px;
`

const StyledText = styled.p`
  margin: 10px 0 20px 0;
  font-size: 0.9rem;
  color: #6c6c6c;
`

const StyledInformationText = styled.p`
  margin-top: 0;
  margin-bottom: 2.5rem;
  font-size: 0.8rem;
  color: #6c6c6c;
`

const StyledSelect = styled(Select).withConfig({
  shouldForwardProp: prop => prop !== 'isPlaceholder'
})`
  flex: 1 0 auto;
  max-width: 500px;
  margin: 0.5rem 0 1rem;
  .field__wrapper .field__input {
    color: ${props => (props.isPlaceholder ? 'gray' : '#000000')};
  }
`

const StyledSlash = styled.p`
  width: 5%;
  font-size: 1.5rem;
  margin-top: 12px;
  margin-left: 2px;
  color: #6c6c6c;
`

const StyledMentionsRichTextEditorWithFormik = styled(MentionsRichTextEditorWithFormik)`
  max-width: ${props => (props.mentions?.length > 0 ? '500px' : '100%')};
`

const StyledContainer = styled.div`
  margin-top: 20px;
  margin-bottom: 10px;
`

const StyledTitle = styled.p`
  font-weight: bold;
  font-size: 1.1rem;
  margin-top: 0.5rem;
  margin-bottom: 0.6rem;
`

const StyledRequestPreviewContainer = styled.div`
  margin-top: 10px;
  margin-bottom: 15px;
  background-color: #d5d3d356;
  border-color: #dad8d8ff;
  border-width: 1px;
  border-style: solid;
  border-radius: 3px;
  padding: 5px;
`

const StyledRequestPreview = styled.p`
  margin-top: 10px;
  margin-bottom: 10px;
  margin-left: 5px;
  font-size: 0.9rem;
`

const StyledSpan = styled.span`
  color: #ff0000;
  margin-right: 5px;
`
//</editor-fold>

const getValidationSchema = (intl: IntlShape, formObject: any) => {
  const result: any = {
    path: string().min(2, intl.formatMessage(messages.endpointPathRequired)),
    dataSourceKey: string().required(intl.formatMessage(messages.sourceRequired))
  }
  // If 'query' is of type array it should contain objects of key/value pairs
  if (Array.isArray(formObject.input?.query)) {
    result.input = object().shape({
      query: array().of(
        object().shape({
          key: string().trim().required(intl.formatMessage(messages.queryNameRequired)),
          value: string().trim().required(intl.formatMessage(messages.queryValueRequired))
        })
      )
    })
  }

  return result
}

const getInitialValues = (stepData: APIRequestStepData): StepData => ({
  dataSourceKey: stepData?.dataSourceKey ?? '',
  path: stepData.path ?? '',
  method: 'get',
  input: get(stepData, 'input', {
    query: []
  })
})
const getAPIRequestOperationHelpUrl = 'https://help.workgrid.com/s/article/Utilizing-the-API-Request-Operation'

const APIRequestOperation = ({ formik, dataSources, readOnly = false }: APIRequestProps & FormikValues) => {
  const intl = useIntl()

  const { getVariablesForStep } = useWorkflowFeature()
  const variables = getVariablesForStep()
  const { spaceId } = useParams<{ spaceId: string }>()
  const addVariableRef = useRef<any>([])
  const [showEndpointVariablePopOver, setShowEndpointVariablePopOver] = useState(false)
  const [datasourceConfig, setDatasourceConfig] = useState<any>()
  const [baseUrl, setBaseUrl] = useState('')

  useEffect(() => {
    if (dataSources?.length === 1) {
      // Pre-set the dataSourceKey if there is only one dataSource
      formik.setFieldValue('stepData.dataSourceKey', dataSources[0].key)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSources])

  type WorkgridCatalogDatasourceQueries = UseQueryOptions<WorkgridCatalogDatasource>[]
  const workgridDatasourceConfigurationQueryResult = useQueries<WorkgridCatalogDatasourceQueries>(
    dataSources?.map((ds: AppInstanceDataSource) => ({
      ...dataSourceV3CatalogQueryOptions({
        spaceId,
        datasourceId: ds.datasourceId || ds.key
      }),
      enabled: !!dataSources,
      onSuccess: (workgridDataSource: WorkgridCatalogDatasource) => {
        if (workgridDataSource?.id === formik.values.stepData.dataSourceKey) {
          setDatasourceConfig(workgridDataSource)
        }
      }
    })) || []
  )

  type WorkgridDatasourceInstanceQueries = UseQueryOptions<WorkgridDatasource>[]
  const dataSourcesQuery = useQueries<WorkgridDatasourceInstanceQueries>(
    dataSources?.map((ds: AppInstanceDataSource) => ({
      ...dataSourceV3QueryOptions({
        spaceId,
        datasourceId: ds.datasourceId || ds.key,
        instanceId: ds.id
      }),
      enabled: !!dataSources
    })) || []
  )

  const dataSourceInstances = dataSourcesQuery.reduce((dataSourcesInstances: WorkgridDatasource[], dataSource) => {
    if (dataSource.data) {
      dataSourcesInstances.push(dataSource.data)
    }
    return dataSourcesInstances
  }, [])

  const getBaseUrl = () => {
    if (!isEmpty(datasourceConfig)) {
      return datasourceConfig?.schema?.properties['baseUrl']?.default || ''
    }
  }

  useEffect(() => {
    let baseUrl = getBaseUrl()
    if (baseUrl) {
      const selectedDataSource = dataSourceInstances.find(
        (ds: WorkgridDatasource) => ds.datasourceId === formik.values.stepData.dataSourceKey
      )
      baseUrl = baseUrl?.replace(new RegExp(/\$?{([^}]+)}/g, 'gi'), (matched: string) => {
        const matchedPlaceholder = matched.replaceAll(/[${}]/g, '')
        return selectedDataSource?.configuration[matchedPlaceholder] || matched
      })
    }
    setBaseUrl(baseUrl || '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasourceConfig, formik.values.stepData.dataSourceKey, dataSources])

  return dataSources.length > 0 ? (
    <>
      {datasourceConfig && (
        <div className="mb-2">
          <a
            href={datasourceConfig.vendorApiUrl}
            rel="noopener noreferrer"
            target="_blank"
            className="flex items-center font-medium text-blue-500 hover:underline dark:text-blue-500"
          >
            Vendor API Documentation
            <ArrowTopRightOnSquareIcon
              aria-hidden="true"
              className="ml-1.5 mb-0.5 h-4 w-4 flex-shrink-0 text-blue-500 hover:underline dark:text-blue-500"
            />
          </a>
        </div>
      )}
      <FormLabel>{intl.formatMessage(messages.method)}</FormLabel>
      <StyledText>{intl.formatMessage(messages.get)}</StyledText>
      <FormLabel>{intl.formatMessage(messages.source)}</FormLabel>
      {workgridDatasourceConfigurationQueryResult.length === 1 ? (
        <StyledText>{workgridDatasourceConfigurationQueryResult[0].data?.name}</StyledText>
      ) : workgridDatasourceConfigurationQueryResult.length > 1 ? (
        <StyledSelect
          disabled={readOnly}
          name="stepData.dataSourceKey"
          className=""
          onChange={(e: any) => {
            if (e.target.name !== 'stepData.dataSourceKey') return
            const key = e.target.value
            formik.setFieldValue('stepData.dataSourceKey', key)
            setDatasourceConfig(null)
            setDatasourceConfig(workgridDatasourceConfigurationQueryResult.find(ds => ds.data?.id === key)?.data)
          }}
          required={true}
          variant={''}
          isPlaceholder={!formik.values.stepData.dataSourceKey}
        >
          <option value={''} disabled selected>
            {intl.formatMessage(messages.dataSourceSelect)}
          </option>
          {dataSources.map((dataSource: DataSource) => (
            <Select.Option className="" key={dataSource.id} value={dataSource.key}>
              {workgridDatasourceConfigurationQueryResult?.find(ds => ds.data?.id === dataSource.key)?.data?.name || ''}
            </Select.Option>
          ))}
        </StyledSelect>
      ) : null}
      {formik.values.stepData.dataSourceKey && (
        <>
          <FormLabel>{intl.formatMessage(messages.baseUrl)}</FormLabel>
          <StyledText>
            <InlineLoader useQueryResult={workgridDatasourceConfigurationQueryResult}>{() => baseUrl}</InlineLoader>
          </StyledText>
        </>
      )}
      <FormLabel>
        <StyledSpan>{intl.formatMessage(messages.requiredStar)}</StyledSpan>
        {intl.formatMessage(messages.endpoint)}
      </FormLabel>
      <StyledRow>
        <StyledSlash>{intl.formatMessage(messages.slash)}</StyledSlash>
        <VariableInputContainer>
          <Row>
            <StyledMentionsRichTextEditorWithFormik
              key="requestEndpoint"
              readOnly={readOnly}
              required={true}
              placeholder={intl.formatMessage(messages.enterEndpoint)}
              setAddMention={addMentionFn => (addVariableRef.current['stepData.path'] = addMentionFn)}
              name={`stepData.path`}
              formik={formik}
              multiLine={false}
              mentions={getAllFlattenedVariables(flatten(variables))}
            />
            {variables.length > 0 && !readOnly && (
              <VariableButton
                onClick={() => {
                  setShowEndpointVariablePopOver(true)
                }}
              >
                <StyledWandIcon title={intl.formatMessage(operationsMessages.addVariableButton)} />
              </VariableButton>
            )}
          </Row>
          {showEndpointVariablePopOver && (
            <VariableOverlay
              onSelectVariable={(variable: Variable) => addVariableRef.current[`stepData.path`](variable)}
              onClose={() => {
                setShowEndpointVariablePopOver(false)
              }}
              variables={variables}
              validVariableTypes={[
                FieldTypes.STRING,
                FieldTypes.BOOLEAN,
                FieldTypes.INTEGER,
                FieldTypes.NUMBER,
                FieldTypes.ANY
              ]}
            />
          )}
        </VariableInputContainer>
      </StyledRow>
      <StyledInformationText>{intl.formatMessage(messages.moreInformation)}</StyledInformationText>
      <QueryParameters
        readOnly={readOnly}
        variables={variables}
        queryParamPath={'stepData.input.query'}
        formik={formik}
      />
      <StyledContainer>
        <StyledTitle>{intl.formatMessage(messages.requestPreview)}</StyledTitle>
        <StyledRequestPreviewContainer>
          <StyledRequestPreview data-testid={'requestPreview'}>
            {baseUrl + getRequestPreview(formik.values.stepData)}
          </StyledRequestPreview>
        </StyledRequestPreviewContainer>
      </StyledContainer>
    </>
  ) : (
    <StyledRow>
      <FormLabel>{intl.formatMessage(messages.apiRequestErrorMessage)}</FormLabel>
    </StyledRow>
  )
}

export default connect(APIRequestOperation)

export { getInitialValues, getValidationSchema, getAPIRequestOperationHelpUrl }
